import { useEffect, useRef } from 'react';
import type { Citation } from '@/lib/types';
import './TranscriptPanel.css';

/**
 * The transcript, opened at a cited passage.
 *
 * This is the other half of cite-to-passage: a citation is only worth anything if clicking it
 * lands you on the sentence. The panel slices the transcript on the citation's character
 * offsets, marks the quoted span, and scrolls it into view.
 *
 * The offsets are computed by the pipeline in code — it locates the model's verbatim
 * `evidence_quote` inside `transcript_text` — so they are exact rather than approximate. If a
 * span ever fails to line up, that is a data problem worth surfacing, not something to paper
 * over with fuzzy matching here.
 */
export function TranscriptPanel({
  transcript,
  citation,
  meetingTitle,
  onClose,
}: {
  transcript: string;
  citation: Citation;
  meetingTitle: string;
  onClose: () => void;
}) {
  const markRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const before = transcript.slice(0, citation.charStart);
  const quoted = transcript.slice(citation.charStart, citation.charEnd);
  const after = transcript.slice(citation.charEnd);

  // The span may be a long way down a two-hour transcript, so put it on screen rather than
  // opening at the top and asking the reader to hunt.
  useEffect(() => {
    closeRef.current?.focus();
    markRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, citation.charStart]);

  const spanMissing = quoted.trim().length === 0;

  return (
    <div
      className="transcript"
      role="dialog"
      aria-modal="true"
      aria-label={`Transcript — ${meetingTitle}`}
    >
      <header className="transcript__head">
        <div>
          <p className="u-meta">Source transcript</p>
          <h2 className="transcript__title">{meetingTitle}</h2>
        </div>
        <button ref={closeRef} type="button" className="transcript__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="transcript__body">
        {spanMissing ? (
          /* Say so rather than showing an unmarked transcript and implying the citation
             checked out. */
          <p className="transcript__missing">
            This citation points to characters {citation.charStart}–{citation.charEnd}, which
            don’t resolve in this transcript. The quoted passage was:
            <br />
            <q>{citation.quote}</q>
          </p>
        ) : (
          <p className="transcript__text">
            {before}
            <mark className="transcript__mark" ref={markRef}>
              {quoted}
            </mark>
            {after}
          </p>
        )}
      </div>
    </div>
  );
}
